import type {
  InventoryItem,
  InventoryItemCategoryId,
  InventoryItemDetailJson,
  RawInventoryItem,
} from 'src/types/itemInventory';

import { diffOfDates } from './date';

export type MaybeRawInventoryItem = RawInventoryItem | InventoryItem;

const parseDetailJson = (detailJson: string | InventoryItemDetailJson): InventoryItemDetailJson => {
  if (typeof detailJson === 'string') {
    return JSON.parse(detailJson || '{}');
  }
  return detailJson ?? {};
};

/**
 * 서버로부터 내려오는 인벤토리 아이템의 detailJson(string)을 파싱하여 InventoryItem으로 변환합니다.
 * 이미 파싱된 객체인 경우에도 안전하게 처리합니다.
 */
export const parseRawInventoryItems = (items: MaybeRawInventoryItem[]): InventoryItem[] =>
  items.map(({ detailJson, ...item }) => ({
    ...item,
    detailJson: parseDetailJson(detailJson),
  }));

/**
 * 인벤토리 아이템이 사용 가능한지 여부를 확인합니다.
 *
 * @param item - 확인할 인벤토리 아이템
 * @returns 아이템을 사용할 수 있으면 true, 아니면 false
 */
export const canUseInventoryItem = (item: InventoryItem) => {
  const today = Date.now();

  // quantity 혹은 dateValidUntil이 없을 경우 사용 횟수 혹은 기한 제한이 없는 항목
  return (
    (item.quantity == null || item.quantity > 0) &&
    (item.dateValidUntil == null || diffOfDates(item.dateValidUntil, today) >= 0)
  );
};

/**
 * 인벤토리에서 특정 카테고리 ID를 가진 아이템이 존재하는지 확인합니다.
 *
 * @param inventory - 인벤토리 아이템 배열
 * @param  categoryId - 확인할 카테고리 ID
 * @returns 해당 카테고리의 아이템이 존재하면 true, 아니면 false
 */
export const hasItemInInventory = (
  inventory: InventoryItem[],
  /* eslint-disable @typescript-eslint/ban-types */
  categoryId: InventoryItemCategoryId | (string & {})
  /* eslint-enable @typescript-eslint/ban-types */
) => {
  return inventory.some((item) => item.categoryId === categoryId && canUseInventoryItem(item));
};

/**
 * dateValidUntil 값을 기준으로 인벤토리 아이템을 오름차순(가장 빠른 날짜가 먼저)으로 정렬합니다.
 *
 * @param a - 첫 번째 인벤토리 아이템
 * @param b - 두 번째 인벤토리 아이템
 * @returns 오름차순 정렬을 위한 비교값
 */
export const sortDateValidUntil = (
  { dateValidUntil: a }: Pick<InventoryItem, 'dateValidUntil'>,
  { dateValidUntil: b }: Pick<InventoryItem, 'dateValidUntil'>
) => {
  if (!a || !b) {
    return a ? -1 : 1;
  }

  return new Date(a).getTime() - new Date(b).getTime();
};
